import React, { useEffect, useState } from 'react';
import { Box, Paper, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { selectStationUnitsData } from '../../../store/stationsAndUnitsReducer/selectors';
import { stationsAndUnitsThunk } from '../../../store/stationsAndUnitsReducer/thunk';
import UnitForm from './UnitForm';
import ButtonTheme from '../../../components/ThemeButton/ThemeButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const Units = ({ station }) => {
  const dispatch = useDispatch();
  const { units, isUnitsLoading } = useSelector(selectStationUnitsData);
  const [showForm, setShowForm] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (station?.id) {
      // Get units for this specific station
      dispatch(stationsAndUnitsThunk.getUnits({}, station.id));
    }
  }, [dispatch, station?.id]);

  useEffect(() => {
    console.log('Units data:', units);
  }, [units]);

  const handleCreateUnit = () => {
    setSelectedUnit(null);
    setIsEditMode(false);
    setShowForm(true);
  };

  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setIsEditMode(true);
    setShowForm(true);
  };

  const handleDeleteUnit = async (unit) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      try {
        await dispatch(stationsAndUnitsThunk.deleteUnit(station.id, unit.id));
        // Refresh units for this specific station
        dispatch(stationsAndUnitsThunk.getUnits({}, station.id));
      } catch (error) {
        console.error('Error deleting unit:', error);
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEditMode && selectedUnit) {
        // Use station-specific endpoint for units within station context
        await dispatch(
          stationsAndUnitsThunk.updateUnit(formData, station.id, selectedUnit.id)
        );
      } else {
        // Use station-specific endpoint for creating units within station context
        await dispatch(stationsAndUnitsThunk.createUnit(formData, station.id));
      }

      // Refresh units for this specific station
      dispatch(stationsAndUnitsThunk.getUnits({}, station.id));
      setShowForm(false);
      setSelectedUnit(null);
    } catch (error) {
      console.error('Error saving unit:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedUnit(null);
    setIsEditMode(false);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Units for Station: {station?.internal_id || station?.station_id}
        </Typography>
        {!showForm && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ButtonTheme
              disabled
              onClick={handleCreateUnit}
              variant="filled"
              color="primary"
              startIcon={<AddIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Add Unit
            </ButtonTheme>
          </Box>
        )}
      </Box>

      {!showForm ? (
        <Paper
          sx={{
            p: '24px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {units && units.length > 0 ? (
            <Box>
              {units.map((unit) => (
                <Paper
                  key={unit.id}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: 2,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          mb: 0.5,
                        }}
                      >
                        {unit.cad_designation_1 ||
                          unit.cad_designation_2 ||
                          `Unit ${unit.id}`}
                      </Box>
                      <Box
                        sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                      >
                        {unit.cad_designation_1 &&
                          unit.cad_designation_2 &&
                          `${unit.cad_designation_1} - ${unit.cad_designation_2}`}
                        {unit.cad_designation_1 &&
                          !unit.cad_designation_2 &&
                          unit.cad_designation_1}
                        {!unit.cad_designation_1 &&
                          unit.cad_designation_2 &&
                          unit.cad_designation_2}
                        {unit.type && ` | Type: ${unit.type}`}
                      </Box>
                      <Box
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          mt: 0.5,
                        }}
                      >
                        Staffing: {unit.staffing || 0}
                        {unit.dedicated_staffing &&
                          ` | Dedicated Staffing: Yes`}
                        {!unit.dedicated_staffing &&
                          ` | Dedicated Staffing: No`}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditUnit(unit)}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        disabled
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteUnit(unit)}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              {isUnitsLoading ? 'Loading units...' : 'No units found for this station'}
            </Box>
          )}
        </Paper>
      ) : (
        <UnitForm
          unit={selectedUnit}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isEdit={isEditMode}
          stationId={station?.id}
        />
      )}
    </Box>
  );
};

export default Units;
